import React from "react";
import { Box, Typography, ButtonBase } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTranslation } from "../../hooks/useTranslation";

interface InjuryStepProps {
  treatedBy: string;
  onChange: (field: string, value: string) => void;
}

const InjuryStep: React.FC<InjuryStepProps> = ({ treatedBy, onChange }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ py: 2 }}>
      {/* Question */}
      <Typography
        variant="h5"
        align="center"
        sx={{
          mb: 4,
          fontWeight: 500,
          color: "#374151",
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
        }}
      >
        {t("form.firstQuestion.question")}
      </Typography>

      {/* Icon Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: { xs: 3, sm: 6 },
          flexWrap: "wrap",
        }}
      >
        {/* Yes Button */}
        <ButtonBase
          onClick={() => {
            onChange("treatedBy", "YES");
            onChange("physicalInjury", "YES");
          }}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            transition: "transform 0.2s ease",
            "&:hover": {
              transform: "scale(1.05)",
            },
          }}
        >
          <Box
            sx={{
              width: { xs: 120, sm: 150 },
              height: { xs: 120, sm: 150 },
              borderRadius: "50%",
              backgroundColor: treatedBy === "YES" ? "#dcfce7" : "#e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              border:
                treatedBy === "YES"
                  ? "3px solid #22c55e"
                  : "3px solid transparent",
              "&:hover": {
                backgroundColor: "#dcfce7",
              },
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: { xs: 60, sm: 80 },
                color: "#22c55e",
              }}
            />
          </Box>
          <Typography
            sx={{
              color: "#0ea5e9",
              fontWeight: 500,
              fontSize: "1.1rem",
              textDecoration: "underline",
              textUnderlineOffset: "4px",
            }}
          >
            {t("form.firstQuestion.option1")}
          </Typography>
        </ButtonBase>

        {/* No Button */}
        <ButtonBase
          onClick={() => {
            onChange("treatedBy", "NO");
            onChange("physicalInjury", "NO");
          }}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            transition: "transform 0.2s ease",
            "&:hover": {
              transform: "scale(1.05)",
            },
          }}
        >
          <Box
            sx={{
              width: { xs: 120, sm: 150 },
              height: { xs: 120, sm: 150 },
              borderRadius: "50%",
              backgroundColor: treatedBy === "NO" ? "#fee2e2" : "#e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              border:
                treatedBy === "NO"
                  ? "3px solid #ef4444"
                  : "3px solid transparent",
              "&:hover": {
                backgroundColor: "#fee2e2",
              },
            }}
          >
            <CancelIcon
              sx={{
                fontSize: { xs: 60, sm: 80 },
                color: "#ef4444",
              }}
            />
          </Box>
          <Typography
            sx={{
              color: "#0ea5e9",
              fontWeight: 500,
              fontSize: "1.1rem",
              textDecoration: "underline",
              textUnderlineOffset: "4px",
            }}
          >
            {t("form.firstQuestion.option2")}
          </Typography>
        </ButtonBase>
      </Box>
    </Box>
  );
};

export default InjuryStep;
